import { msg } from '@lingui/core/macro';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { getAccessOptions, getVisibilityOptions } from 'modules/template-editor/domain/consts/form/template/options';
import { templatePresets, TemplatePresetsNameList } from 'modules/template-editor/domain/consts/form/template/template-presets';
import { Values } from 'modules/template-editor/domain/interface/form/template';
import TemplateForm from 'modules/template-editor/ui/components/elements/forms/template-form';
import { useLingui } from '@lingui/react/macro';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import { copyTemplate } from 'modules/template-editor/store/templates/actions';
import { resetPressedState } from 'modules/template-editor/store/templates/templates';
import useTemplates from 'modules/template-editor/ui/hooks/use-templates';

const PRESET: TemplatePresetsNameList = 'duplicate';

function useDuplicateModal() {
  const { t } = useLingui();
  const dispatch = useDispatch();
  const dispatchWithToast = useDispatchWithToast();
  const { info } = useOrganizer();
  const { activeTemplate, templates, isProcessed, isFinished, currentProcess } = useTemplates();
  const isAdmin = useIsAdmin();

  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onSubmit = useCallback(
    // `async` + промис запроса — Formik держит кнопку формы запертой до ответа (IMGM-1561).
    async (values: Values) => {
      if (!activeTemplate) return;
      if (!info) return;
      const { width, height, name } = values;
      const resolution = { width: +width, height: +height };
      return dispatchWithToast(
        copyTemplate({
          templateId: activeTemplate.id,
          ownerId: info.id,
          resolution,
          name: name.toString().trim(),
          process: 'duplicate template',
        }),
        { error: msg`Не удалось создать копию` },
      );
    },
    [activeTemplate, dispatchWithToast, info],
  );

  const initialValue = useMemo(
    () => ({
      // eslint-disable-next-line lingui/no-unlocalized-strings -- имя копии уходит в базу, это данные, а не подпись интерфейса
      name: activeTemplate?.name ? activeTemplate?.name + ' (дубликат)' : '',
      width: activeTemplate?.resolution.width?.toString() ?? '',
      height: activeTemplate?.resolution.height?.toString() ?? '',
      visibility: getVisibilityOptions().find((option) => option.value === activeTemplate?.visibility) ?? getVisibilityOptions()[0],
      access: getAccessOptions().find((option) => option.value === activeTemplate?.access) ?? getAccessOptions()[0],
    }),
    [activeTemplate],
  );

  const existingNames = useMemo(() => templates.map((template) => template.name.trim()), [templates]);

  useEffect(() => {
    if (!isFinished) {
      return;
    }
    if (currentProcess !== 'duplicate template') {
      return;
    }
    close();
    dispatch(resetPressedState());
  }, [isFinished, close, dispatch, currentProcess]);

  return {
    modal: (
      <Modal show={isOpen} onClose={close} size="md">
        <UDText type={'title-3'} className="mb-5">
          {t(templatePresets[PRESET].header)}
        </UDText>
        <TemplateForm
          preset={PRESET}
          initialValues={initialValue}
          onSubmit={onSubmit}
          existingNames={existingNames}
          advancedAccess={isAdmin}
          isLoading={isProcessed}
        />
      </Modal>
    ),
    close,
    open,
  };
}

export default useDuplicateModal;
